import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { get, post, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { cn, dateOnly, dateTime, humanize, timeOnly } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, Field, SkeletonText, Input, StatusBadge, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { TriageChip } from './TriageChip';
import { InvestigationOrderPanel } from '../clinical/InvestigationOrderPanel';
import { InvestigationList, investigationsQueryKey } from '../clinical/InvestigationList';
import { MAX_MLC_REASON, canIdentify, canSaveMlc, type ErCaseDetail } from './er-board';

const LINK_CLASS = 'text-primary underline-offset-4 hover:underline';

function ageText(patient: { dob: string | null; approximateAgeYears: number | null }): string {
  if (patient.dob) return tr('opd', 'er_born_x', { date: dateOnly(patient.dob) });
  return patient.approximateAgeYears !== null
    ? tr('opd', 'er_about_x_years', { years: patient.approximateAgeYears })
    : tr('opd', 'er_age_unknown');
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Reading({ abnormal, children }: { abnormal: boolean; children: ReactNode }) {
  return <span className={cn('tabular', abnormal && 'font-semibold text-destructive')}>{children}</span>;
}

export function ErCaseDialog({ erCaseId, onClose }: { erCaseId: string; onClose: () => void }) {
  const { can, hasModule } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editingMlc, setEditingMlc] = useState(false);

  const detail = useQuery({
    queryKey: ['er-case', erCaseId],
    queryFn: () => get<ErCaseDetail>(`/er/cases/${erCaseId}`),
  });

  const printSummary = useMutation({
    mutationFn: () => get<{ html: string }>(`/er/cases/${erCaseId}/print?format=html`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('opd', 'er_could_not_print_the_summary'), describeError(error)),
  });

  const certificate = useMutation({
    mutationFn: async () => {
      const issued = await post<{ id: string }>('/mortality/certificate', { erCaseId });
      return get<{ html: string }>(`/certificates/${issued.id}/print`);
    },
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('opd', 'er_could_not_issue_the_death_certificate'), describeError(error)),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['er-case', erCaseId] });
    void queryClient.invalidateQueries({ queryKey: ['er-board'] });
  };

  const c = detail.data;
  const mayIdentify = can('er.manage', 'patient.update');
  const mayIssueCertificate = Boolean(c && c.disposition === 'expired' && c.patientId && c.death) && can('certificate.issue');

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title={c ? tr('opd', 'er_emergency_case_x', { number: c.number }) : tr('opd', 'er_emergency_case')}
      description={c ? (c.patient?.fullName ?? c.unknownPatientLabel ?? tr('opd', 'er_unidentified_arrival')) : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('opd', 'close')}
          </Button>
          {mayIssueCertificate && (
            <Button variant="outline" onClick={() => certificate.mutate()} loading={certificate.isPending}>
              {tr('opd', 'er_issue_and_print_death_certificate')}
            </Button>
          )}
          <Button variant="outline" onClick={() => printSummary.mutate()} loading={printSummary.isPending} disabled={!c}>
            <Printer className="h-4 w-4" /> {tr('opd', 'er_print_summary')}
          </Button>
        </>
      }
    >
      {detail.isLoading && (
        <SkeletonText lines={3} />
      )}
      {detail.error && <Alert variant="destructive">{describeError(detail.error)}</Alert>}

      {c && (
        <div className="space-y-5">
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <Fact label={tr('opd', 'patient')}>
              {c.patient ? (
                <>
                  <span className="font-medium">{c.patient.fullName}</span>
                  {c.patient.isDeceased && (
                    <Badge variant="destructive" className="ms-1">
                      {tr('opd', 'er_deceased')}
                    </Badge>
                  )}
                  <span className="block text-xs text-muted-foreground">
                    {c.patient.mrn} · {humanize(c.patient.sex)} · {ageText(c.patient)}
                    {c.patient.phone ? ` · ${c.patient.phone}` : ''}
                  </span>
                </>
              ) : (
                <>
                  <span className="font-medium">{c.unknownPatientLabel ?? tr('opd', 'er_unidentified_arrival')}</span>
                  <span className="block text-xs text-muted-foreground">{tr('opd', 'er_not_identified')}</span>
                </>
              )}
            </Fact>
            <Fact label={tr('opd', 'arrival')}>
              {humanize(c.arrivalMode)} · {dateTime(c.arrivedAt)}
              <span className="block text-xs text-muted-foreground">
                {c.chiefComplaint ?? tr('opd', 'er_no_complaint_recorded')}
                {c.broughtByName
                  ? ` · ${
                      c.broughtByPhone
                        ? tr('opd', 'er_brought_by_x_phone', { name: c.broughtByName, phone: c.broughtByPhone })
                        : tr('opd', 'er_brought_by_x', { name: c.broughtByName })
                    }`
                  : ''}
              </span>
            </Fact>
            <Fact label={tr('opd', 'triage')}>
              <TriageChip level={c.triageLevel} />
              <span className="ms-2 text-xs text-muted-foreground">
                {c.bayLabel ? tr('opd', 'er_bay_x', { bay: c.bayLabel }) : tr('opd', 'er_no_bay')}
                {c.triagedAt ? ` · ${tr('opd', 'er_triaged_x', { time: timeOnly(c.triagedAt) })}` : ''}
              </span>
            </Fact>
            <Fact label={tr('opd', 'doctor')}>
              {c.attendingDoctor ? (
                <>
                  {c.attendingDoctor.name}
                  {c.attendingDoctor.designation ? `, ${c.attendingDoctor.designation}` : ''}
                </>
              ) : (
                <span className="text-muted-foreground">{tr('opd', 'er_not_yet_seen')}</span>
              )}
              {c.seenByDoctorAt && (
                <span className="block text-xs text-muted-foreground">{tr('opd', 'er_seen_x', { time: dateTime(c.seenByDoctorAt) })}</span>
              )}
            </Fact>
            <Fact label={c.disposition ? tr('opd', 'er_time_in_the_department') : tr('opd', 'waiting')}>
              {c.waitingMinutes} {tr('opd', 'min')}
            </Fact>
            <Fact label={tr('opd', 'outcome')}>
              {c.disposition ? (
                <>
                  {humanize(c.disposition)}
                  {c.disposedAt ? ` · ${dateTime(c.disposedAt)}` : ''}
                  {c.referredTo && (
                    <span className="block text-xs text-muted-foreground">{tr('opd', 'er_referred_to_x', { place: c.referredTo })}</span>
                  )}
                  {c.dispositionNotes && (
                    <span className="block whitespace-pre-wrap text-xs text-muted-foreground">{c.dispositionNotes}</span>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">{tr('opd', 'er_still_in_the_department')}</span>
              )}
            </Fact>
            {c.admission && (
              <Fact label={tr('opd', 'admission')}>
                <Link to={`/ipd/admissions/${c.admission.id}`} className={LINK_CLASS}>
                  {c.admission.number}
                </Link>{' '}
                <StatusBadge status={c.admission.status} />
              </Fact>
            )}
            {c.death && (
              <Fact label={tr('opd', 'er_death_recorded')}>
                {c.death.causeOfDeath}
                <span className="block text-xs text-muted-foreground">{dateTime(c.death.deceasedAt)}</span>
              </Fact>
            )}
            {c.registrationInvoice && (
              <Fact label={tr('opd', 'er_registration_invoice')}>
                <Link to={`/billing/invoices/${c.registrationInvoice.id}`} className={LINK_CLASS}>
                  {c.registrationInvoice.number}
                </Link>{' '}
                <StatusBadge status={c.registrationInvoice.status} />
              </Fact>
            )}
          </dl>

          {c.patientId === null && mayIdentify && (
            <IdentifyForm
              erCase={c}
              onIdentified={() => {
                void queryClient.invalidateQueries({ queryKey: ['patient'] });
                refresh();
              }}
            />
          )}

          {c.triageNotes && (
            <Section title={tr('opd', 'er_triage_notes')}>
              <p className="whitespace-pre-wrap text-sm">{c.triageNotes}</p>
            </Section>
          )}

          <Section
            title={tr('opd', 'medico_legal')}
            action={
              can('er.manage') &&
              !editingMlc && (
                <Button size="sm" variant="outline" onClick={() => setEditingMlc(true)}>
                  {tr('opd', 'er_edit_mlc_flag')}
                </Button>
              )
            }
          >
            {editingMlc ? (
              <MlcForm
                erCase={c}
                onCancel={() => setEditingMlc(false)}
                onSaved={() => {
                  setEditingMlc(false);
                  refresh();
                }}
              />
            ) : c.isMlc ? (
              <div>
                <Badge variant="destructive">{tr('opd', 'er_mlc')}</Badge>
                <p className="mt-1 whitespace-pre-wrap text-sm">{c.mlcDetails ?? tr('opd', 'er_no_details_recorded')}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{tr('opd', 'er_not_a_medico_legal_case')}</p>
            )}
          </Section>

          <Section title={tr('opd', 'er_vital_signs')}>
            <DataTable
              dense
              rows={c.vitals}
              rowKey={(row) => row.id}
              empty={c.patient ? tr('opd', 'er_no_vitals_since_arrival') : tr('opd', 'er_vitals_after_identification')}
              columns={[
                { header: tr('opd', 'er_recorded'), cell: (row) => dateTime(row.recordedAt) },
                {
                  header: tr('opd', 'bp'),
                  cell: (row) => (
                    <Reading abnormal={row.isAbnormal}>
                      {row.bpSystolic !== null && row.bpDiastolic !== null ? `${row.bpSystolic}/${row.bpDiastolic}` : '—'}
                    </Reading>
                  ),
                },
                { header: tr('opd', 'pulse'), align: 'right', cell: (row) => <Reading abnormal={row.isAbnormal}>{row.pulse ?? '—'}</Reading> },
                {
                  header: tr('opd', 'er_resp'),
                  align: 'right',
                  cell: (row) => <Reading abnormal={row.isAbnormal}>{row.respiratoryRate ?? '—'}</Reading>,
                },
                {
                  header: tr('opd', 'temp'),
                  align: 'right',
                  cell: (row) => <Reading abnormal={row.isAbnormal}>{row.temperatureC ?? '—'}</Reading>,
                },
                {
                  header: tr('opd', 'spo'),
                  align: 'right',
                  cell: (row) => <Reading abnormal={row.isAbnormal}>{row.spo2 !== null ? `${row.spo2}%` : '—'}</Reading>,
                },
              ]}
            />
          </Section>

          {c.patientId && (hasModule('lab') || hasModule('radiology')) && (
            <Section title={tr('opd', 'investigations')}>
              <div className="space-y-4">
                {can('order.write') && !c.disposition && (
                  <InvestigationOrderPanel
                    patientId={c.patientId}
                    erCaseId={c.id}
                    defaultPriority="stat"
                    onSaved={() => {
                      void queryClient.invalidateQueries({ queryKey: investigationsQueryKey(`/er/cases/${c.id}/investigations`) });
                      refresh();
                    }}
                  />
                )}
                <InvestigationList path={`/er/cases/${c.id}/investigations`} patientId={c.patientId} />
              </div>
            </Section>
          )}

          <Section title={tr('opd', 'orders')}>
            <DataTable
              dense
              rows={c.orders}
              rowKey={(row) => row.id}
              empty={tr('opd', 'er_no_orders_since_arrival')}
              columns={[
                { header: tr('opd', 'er_ordered'), cell: (row) => dateTime(row.orderedAt) },
                { header: tr('opd', 'type'), cell: (row) => humanize(row.type) },
                { header: tr('opd', 'er_items'), cell: (row) => row.items.map((item) => item.label).join(', ') || '—' },
                { header: tr('opd', 'status'), cell: (row) => <StatusBadge status={row.status} /> },
              ]}
            />
          </Section>
        </div>
      )}
    </Dialog>
  );
}

function MlcForm({
  erCase,
  onCancel,
  onSaved,
}: {
  erCase: ErCaseDetail;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [isMlc, setIsMlc] = useState(erCase.isMlc);
  const [details, setDetails] = useState(erCase.mlcDetails ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      put(`/er/cases/${erCase.id}/mlc`, {
        isMlc,
        mlcDetails: isMlc ? details.trim() : null,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      toast.success(tr('opd', 'er_mlc_flag_updated'), tr('opd', 'er_mlc_change_audited'));
      onSaved();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <Checkbox checked={isMlc} onCheckedChange={setIsMlc} label={tr('opd', 'er_medico_legal_case')} />
      {isMlc && (
        <Field label={tr('opd', 'er_mlc_details')} required hint={tr('opd', 'er_mlc_details_hint')}>
          <Textarea rows={2} maxLength={2000} value={details} onChange={(e) => setDetails(e.target.value)} />
        </Field>
      )}
      <Field label={tr('opd', 'er_reason_for_the_change')} required hint={tr('opd', 'er_mlc_reason_hint')}>
        <Input maxLength={MAX_MLC_REASON} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      {error && <Alert variant="destructive">{error}</Alert>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {tr('opd', 'cancel')}
        </Button>
        <Button
          size="sm"
          onClick={() => {
            setError(null);
            save.mutate();
          }}
          loading={save.isPending}
          disabled={!canSaveMlc({ isMlc, mlcDetails: details, reason })}
        >
          {tr('opd', 'er_save_flag')}
        </Button>
      </div>
    </div>
  );
}

function IdentifyForm({ erCase, onIdentified }: { erCase: ErCaseDetail; onIdentified: () => void }) {
  const toast = useToast();
  const attachesDeath = erCase.disposition === 'expired';
  const [patientId, setPatientId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [basis, setBasis] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [tempAge, setTempAge] = useState('');
  const [tempSex, setTempSex] = useState<'male' | 'female' | 'unknown'>('unknown');

  const registerTemporary = useMutation({
    mutationFn: async () => {
      const registered = await post<{ id: string }>('/patients', temporaryPatientFor(erCase, tempSex, tempAge));
      await post(`/er/cases/${erCase.id}/identify`, { patientId: registered.id, confirmed: false });
    },
    onSuccess: () => {
      toast.success(tr('opd', 'er_temporary_patient_registered'), tr('opd', 'er_temporary_patient_registered_detail'));
      onIdentified();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const identify = useMutation({
    mutationFn: () =>
      post(`/er/cases/${erCase.id}/identify`, {
        patientId,
        confirmed,
        basis: attachesDeath ? basis.trim() : undefined,
      }),
    onSuccess: () => {
      toast.success(
        tr('opd', 'er_patient_identified'),
        attachesDeath ? tr('opd', 'er_death_now_on_record') : tr('opd', 'er_case_linked_to_record'),
      );
      onIdentified();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Section title={tr('opd', 'er_identify_the_patient')}>
      <div className="space-y-3 rounded-lg border p-3">
        {attachesDeath ? (
          <Alert variant="warning">{tr('opd', 'er_identify_death_warning')}</Alert>
        ) : (
          <p className="text-sm text-muted-foreground">{tr('opd', 'er_identify_link_hint')}</p>
        )}
        <Field label={tr('opd', 'emr_group_record')} required>
          <PatientPicker value={patientId} onChange={setPatientId} />
        </Field>
        {attachesDeath && (
          <>
            <Checkbox checked={confirmed} onCheckedChange={setConfirmed} label={tr('opd', 'er_confirmed_person_who_died')} />
            <Field label={tr('opd', 'er_identity_basis')} required hint={tr('opd', 'er_identity_basis_hint')}>
              <Input maxLength={300} value={basis} onChange={(e) => setBasis(e.target.value)} />
            </Field>
          </>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => {
              setError(null);
              identify.mutate();
            }}
            loading={identify.isPending}
            disabled={!canIdentify({ patientId, attachesDeath, confirmed, basis })}
          >
            {tr('opd', 'er_identify_patient')}
          </Button>
        </div>
        {!attachesDeath && (
          <div className="space-y-2 border-t pt-3">
            <p className="text-sm text-muted-foreground">{tr('opd', 'er_temporary_patient_hint')}</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <Field label={tr('opd', 'er_estimated_age')}>
                <Input type="number" min={0} max={130} value={tempAge} onChange={(e) => setTempAge(e.target.value)} />
              </Field>
              <Field label={tr('opd', 'sex')}>
                <ChoiceButtons<typeof tempSex>
                  size="sm"
                  value={tempSex}
                  onChange={setTempSex}
                  options={[
                    { value: 'unknown', label: tr('opd', 'unknown') },
                    { value: 'male', label: tr('opd', 'male') },
                    { value: 'female', label: tr('opd', 'female') },
                  ]}
                />
              </Field>
              <div className="flex items-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setError(null);
                    registerTemporary.mutate();
                  }}
                  loading={registerTemporary.isPending}
                >
                  {tr('opd', 'er_register_temporary_patient')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

export function temporaryPatientFor(erCase: Pick<ErCaseDetail, 'number' | 'unknownPatientLabel'>, sex: 'male' | 'female' | 'unknown', age: string) {
  const years = Number.parseInt(age, 10);
  return {
    firstName: (erCase.unknownPatientLabel ?? 'Unidentified').slice(0, 80),
    lastName: `ER ${erCase.number}`.slice(0, 80),
    sex,
    approximateAgeYears: Number.isFinite(years) && years >= 0 && years <= 130 ? years : 30,
    idMissingReason: 'unidentified' as const,
    acknowledgeDuplicate: true,
  };
}
